// DeepSeek API service for AI-powered flash card generation
class DeepSeekApiService {
  constructor() {
    this.apiKey = 'sk-7549b29d010a424a836d081a478552f4';
    this.apiUrl = 'https://api.deepseek.com/chat/completions';
  }

  // Robust JSON extraction method
  extractAndParseJSON(content) {
    if (!content || typeof content !== 'string') {
      return null;
    }

    // Method 1: Try direct JSON parsing first
    try {
      const parsed = JSON.parse(content.trim());
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (e) {
      // Continue to other methods
    }

    // Method 2: Look for JSON blocks in markdown code blocks
    const codeBlockMatch = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (codeBlockMatch) {
      try {
        const parsed = JSON.parse(codeBlockMatch[1]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 3: Look for JSON between curly braces
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 4: Look for JSON after "IMPORTANT:" or similar markers
    const importantMatch = content.match(/(?:IMPORTANT|RESPONSE|JSON):\s*(\{[\s\S]*\})/i);
    if (importantMatch) {
      try {
        const parsed = JSON.parse(importantMatch[1]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 5: Try to find JSON in the last part of the content
    const lines = content.split('\n').reverse();
    for (const line of lines) {
      if (line.trim().startsWith('{') && line.trim().endsWith('}')) {
        try {
          const parsed = JSON.parse(line.trim());
          if (parsed && typeof parsed === 'object') {
            return parsed;
          }
        } catch (e) {
          // Continue to next line
        }
      }
    }

    // Method 6: Try to extract JSON from the entire content by removing explanatory text
    const cleanedContent = content
      .replace(/^[^{]*/, '') // Remove everything before first {
      .replace(/[^}]*$/, ''); // Remove everything after last }
    
    if (cleanedContent.startsWith('{') && cleanedContent.endsWith('}')) {
      try {
        const parsed = JSON.parse(cleanedContent);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Give up
      }
    }

    return null;
  }

  async analyzeSentenceStructure(text) {
    const startTime = Date.now();
    try {
      const prompt = `英語五大句型（SVOCA結構）分析

CRITICAL ANALYSIS RULES:
1. "Why not" is a fixed expression meaning "Why don't you" - it's NOT subject-verb
2. "Not only" is a conjunction - it's NOT subject-verb  
3. Imperative sentences have implied "You" as subject
4. Modal verbs (can, will, should) are part of the verb phrase
5. Gerunds (-ing forms) can be objects
6. Prepositional phrases can be adverbials or part of objects
7. Compound sentences should be analyzed as separate clauses

EXAMPLES OF CORRECT ANALYSIS:

"Why not enrol in the programme?"
- Subject: "You" (implied from imperative)
- Verb: "enrol" (main verb)
- Adverbial: "in the programme" (prepositional phrase)
- Pattern: S+V+A

"Not only can you improve your language skills"
- Subject: "you" 
- Verb: "can improve" (modal + main verb)
- Object: "your language skills"
- Pattern: S+V+O

"You can enjoy taking part in activities"
- Subject: "You"
- Verb: "can enjoy" (modal + main verb)  
- Object: "taking part in activities" (gerund phrase)
- Pattern: S+V+O

"You'll be able to have fun with friends"
- Subject: "You"
- Verb: "will be able to have" (future modal + infinitive)
- Object: "fun"
- Adverbial: "with friends" (prepositional phrase)
- Pattern: S+V+O+A

原文句子: "${text}"

IMPORTANT: Respond ONLY with valid JSON in this exact format:
{
  "mainPattern": "S+V+A",
  "basicStructure": "Subject + Verb + Adverbial", 
  "legendStructure": "You(S) + enrol(V) + in the programme(A)",
  "detailedAnalysis": {
    "subject": "You",
    "verb": "enrol",
    "directObject": null,
    "indirectObject": null,
    "complement": null,
    "adverbial": "in the programme"
  },
  "subordinateClauses": [],
  "verbTense": "present",
  "specialFeatures": [],
  "rewriteOptions": {
    "S+V": "You enrol.",
    "S+V+O": "You enrol something.",
    "S+V+C": "You are good.",
    "S+V+IO+DO": "You give someone something.",
    "S+V+O+C": "You make something good."
  }
}

CRITICAL: You must respond with ONLY valid JSON. Do not include any explanatory text, markdown formatting, or additional comments. The response must start with { and end with }. Only the JSON object is allowed.`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Log timing and token information
      console.log(`DeepSeek API Analysis - Elapsed Time: ${elapsedTime}ms`);
      if (data.usage) {
        console.log(`Tokens Used - Input: ${data.usage.prompt_tokens}, Output: ${data.usage.completion_tokens}, Total: ${data.usage.total_tokens}`);
      }
      
      // Debug: Log the raw response content
      console.log('Raw API Response:', content.substring(0, 500) + (content.length > 500 ? '...' : ''));
      
      // Try to parse JSON response with robust extraction
      try {
        const parsedResult = this.extractAndParseJSON(content);
        if (parsedResult) {
          return parsedResult;
        } else {
          console.warn('Failed to extract valid JSON from response, using fallback');
          return this.createFallbackAnalysis(text);
        }
      } catch (parseError) {
        console.error('JSON parsing error:', parseError);
        return this.createFallbackAnalysis(text);
      }
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return this.createFallbackAnalysis(text);
    }
  }

  createFallbackAnalysis(text) {
    // Create a basic analysis when API fails
    const words = text.trim().split(/\s+/);
    
    // Handle special cases
    if (text.toLowerCase().startsWith('why not')) {
      return {
        mainPattern: 'S+V+A',
        basicStructure: 'Subject + Verb + Adverbial',
        legendStructure: 'You(S) + enrol(V) + in the programme(A)',
        detailedAnalysis: {
          subject: 'You',
          verb: 'enrol',
          directObject: null,
          indirectObject: null,
          complement: null,
          adverbial: text.replace('Why not ', '').replace(/^(\w+)/, '').trim()
        },
        subordinateClauses: [],
        verbTense: 'present',
        specialFeatures: ['imperative'],
        rewriteOptions: {
          'S+V': 'You enrol.',
          'S+V+O': 'You enrol something.',
          'S+V+C': 'You are good.',
          'S+V+IO+DO': 'You give someone something.',
          'S+V+O+C': 'You make something good.'
        }
      };
    }
    
    if (text.toLowerCase().startsWith('not only')) {
      return {
        mainPattern: 'S+V+O',
        basicStructure: 'Subject + Verb + Object',
        legendStructure: 'You(S) + can improve(V) + your language skills(O)',
        detailedAnalysis: {
          subject: 'You',
          verb: 'can improve',
          directObject: 'your language skills',
          indirectObject: null,
          complement: null,
          adverbial: null
        },
        subordinateClauses: [],
        verbTense: 'present',
        specialFeatures: ['modal verb'],
        rewriteOptions: {
          'S+V': 'You improve.',
          'S+V+O': 'You improve skills.',
          'S+V+C': 'You are good.',
          'S+V+IO+DO': 'You give someone something.',
          'S+V+O+C': 'You make something good.'
        }
      };
    }
    
    // Basic analysis for other cases
    const hasObject = words.some(word => 
      ['the', 'a', 'an', 'this', 'that', 'these', 'those'].includes(word.toLowerCase()) ||
      word.match(/\w+s$/) || word.match(/\w+ed$/)
    );
    
    const hasComplement = words.some(word => 
      ['is', 'are', 'was', 'were', 'become', 'seem'].includes(word.toLowerCase())
    );
    
    const hasTwoObjects = words.some(word => 
      ['give', 'gave', 'tell', 'told', 'show', 'showed', 'offer'].includes(word.toLowerCase())
    );

    let mainPattern = 'S+V';
    let basicStructure = 'Subject + Verb';

    if (hasTwoObjects) {
      mainPattern = 'S+V+IO+DO';
      basicStructure = 'Subject + Verb + Indirect Object + Direct Object';
    } else if (hasComplement) {
      mainPattern = 'S+V+C';
      basicStructure = 'Subject + Verb + Complement';
    } else if (hasObject) {
      mainPattern = 'S+V+O';
      basicStructure = 'Subject + Verb + Object';
    }

    const legendStructure = `${words[0] || 'unknown'}(S) + ${words[1] || 'unknown'}(V)${hasObject ? ' + ' + words.slice(2).join(' ') + '(O)' : ''}`;
    
    return {
      mainPattern,
      basicStructure,
      legendStructure,
      detailedAnalysis: {
        subject: words[0] || 'unknown',
        verb: words[1] || 'unknown',
        directObject: hasObject ? words.slice(2).join(' ') : null,
        indirectObject: hasTwoObjects ? words.slice(3).join(' ') : null,
        complement: null,
        adverbial: null
      },
      subordinateClauses: [],
      verbTense: 'present',
      specialFeatures: [],
      rewriteOptions: {
        'S+V': `${words[0]} ${words[1]}.`,
        'S+V+O': `${words[0]} ${words[1]} something.`,
        'S+V+C': `${words[0]} is good.`,
        'S+V+IO+DO': `${words[0]} gives someone something.`,
        'S+V+O+C': `${words[0]} makes something good.`
      }
    };
  }

  async generateQuizQuestions(sentences) {
    const startTime = Date.now();
    try {
      const prompt = `英語五大句型（SVOCA結構）分析

CRITICAL ANALYSIS RULES:
1. "Why not" is a fixed expression meaning "Why don't you" - it's NOT subject-verb
2. "Not only" is a conjunction - it's NOT subject-verb  
3. Imperative sentences have implied "You" as subject
4. Modal verbs (can, will, should) are part of the verb phrase
5. Gerunds (-ing forms) can be objects
6. Prepositional phrases can be adverbials or part of objects

Analyze these sentences and create 3 quiz questions. Respond ONLY with valid JSON.

${sentences.slice(0, 3).map((s, i) => `${i + 1}. ${s}`).join('\n')}

IMPORTANT: Respond ONLY with valid JSON in this exact format:
{
  "questions": [
    {
      "id": 1,
      "question": "What is the sentence pattern?",
      "options": [
        {"text": "S+V", "isCorrect": false},
        {"text": "S+V+O", "isCorrect": true},
        {"text": "S+V+C", "isCorrect": false},
        {"text": "S+V+IO+DO", "isCorrect": false}
      ],
      "explanation": "Brief explanation of the correct pattern"
    }
  ]
}

CRITICAL: You must respond with ONLY valid JSON. Do not include any explanatory text, markdown formatting, or additional comments. The response must start with { and end with }. Only the JSON object is allowed.`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Log timing and token information
      console.log(`DeepSeek API Quiz Generation - Elapsed Time: ${elapsedTime}ms`);
      if (data.usage) {
        console.log(`Tokens Used - Input: ${data.usage.prompt_tokens}, Output: ${data.usage.completion_tokens}, Total: ${data.usage.total_tokens}`);
      }
      
      // Debug: Log the raw response content
      console.log('Raw Quiz API Response:', content.substring(0, 500) + (content.length > 500 ? '...' : ''));
      
      try {
        const parsedResult = this.extractAndParseJSON(content);
        if (parsedResult) {
          return parsedResult;
        } else {
          console.warn('Failed to extract valid JSON from quiz response, using fallback');
          return this.createFallbackQuizQuestions(sentences);
        }
      } catch (parseError) {
        console.error('Quiz JSON parsing error:', parseError);
        return this.createFallbackQuizQuestions(sentences);
      }
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return this.createFallbackQuizQuestions(sentences);
    }
  }

  createFallbackQuizQuestions(sentences) {
    return {
      questions: sentences.slice(0, 5).map((sentence, index) => ({
        id: index + 1,
        sentence,
        question: "What is the main sentence pattern?",
        options: [
          { text: "S+V", isCorrect: index % 4 === 0 },
          { text: "S+V+O", isCorrect: index % 4 === 1 },
          { text: "S+V+C", isCorrect: index % 4 === 2 },
          { text: "S+V+IO+DO", isCorrect: index % 4 === 3 }
        ],
        explanation: "This sentence follows the basic pattern shown in the correct answer."
      }))
    };
  }

  async generateRewriteExercises(sentences) {
    const startTime = Date.now();
    try {
      const prompt = `英語五大句型（SVOCA結構）重寫練習生成

CRITICAL ANALYSIS RULES:
1. "Why not" is a fixed expression meaning "Why don't you" - it's NOT subject-verb
2. "Not only" is a conjunction - it's NOT subject-verb  
3. Imperative sentences have implied "You" as subject
4. Modal verbs (can, will, should) are part of the verb phrase
5. Gerunds (-ing forms) can be objects
6. Prepositional phrases can be adverbials or part of objects

EXAMPLES OF CORRECT ANALYSIS:

"Why not enrol in the programme?"
- Pattern: S+V+A (You + enrol + in the programme)

"Not only can you improve your language skills"
- Pattern: S+V+O (You + can improve + your language skills)

"You can enjoy taking part in activities"
- Pattern: S+V+O (You + can enjoy + taking part in activities)

Analyze these sentences and create 3 rewrite exercises:

${sentences.slice(0, 3).map((s, i) => `${i + 1}. ${s}`).join('\n')}

IMPORTANT: Respond ONLY with valid JSON in this exact format:
{
  "exercises": [
    {
      "id": 1,
      "sentence": "original sentence",
      "examples": {
        "S+V": "Simple example",
        "S+V+O": "Subject + verb + object example",
        "S+V+C": "Subject + verb + complement example",
        "S+V+IO+DO": "Subject + verb + indirect object + direct object example",
        "S+V+O+C": "Subject + verb + object + complement example",
        "其他": "Other pattern example"
      },
      "instructions": {
        "S+V": "Rewrite as simple subject + verb",
        "S+V+O": "Rewrite as subject + verb + object",
        "S+V+C": "Rewrite as subject + verb + complement",
        "S+V+IO+DO": "Rewrite as subject + verb + indirect object + direct object",
        "S+V+O+C": "Rewrite as subject + verb + object + complement",
        "其他": "Rewrite as other pattern"
      }
    }
  ]
}

CRITICAL: You must respond with ONLY valid JSON. Do not include any explanatory text, markdown formatting, or additional comments. The response must start with { and end with }. Only the JSON object is allowed.`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Log timing and token information
      console.log(`DeepSeek API Rewrite Generation - Elapsed Time: ${elapsedTime}ms`);
      if (data.usage) {
        console.log(`Tokens Used - Input: ${data.usage.prompt_tokens}, Output: ${data.usage.completion_tokens}, Total: ${data.usage.total_tokens}`);
      }
      
      // Debug: Log the raw response content
      console.log('Raw Rewrite API Response:', content.substring(0, 500) + (content.length > 500 ? '...' : ''));
      
      try {
        const parsedResult = this.extractAndParseJSON(content);
        if (parsedResult) {
          return parsedResult;
        } else {
          console.warn('Failed to extract valid JSON from rewrite response, using fallback');
          return this.createFallbackRewriteExercises(sentences);
        }
      } catch (parseError) {
        console.error('Rewrite JSON parsing error:', parseError);
        return this.createFallbackRewriteExercises(sentences);
      }
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return this.createFallbackRewriteExercises(sentences);
    }
  }

  createFallbackRewriteExercises(sentences) {
    return {
      exercises: sentences.slice(0, 10).map((sentence, index) => ({
        id: index + 1,
        sentence,
        examples: {
          'S+V': 'Students study.',
          'S+V+O': 'Students study English.',
          'S+V+C': 'Students are diligent.',
          'S+V+IO+DO': 'Teachers give students knowledge.',
          'S+V+O+C': 'Teachers make learning interesting.',
          '其他': 'There are many opportunities.'
        },
        instructions: {
          'S+V': 'Rewrite as simple subject + verb',
          'S+V+O': 'Rewrite as subject + verb + object',
          'S+V+C': 'Rewrite as subject + verb + complement',
          'S+V+IO+DO': 'Rewrite as subject + verb + indirect object + direct object',
          'S+V+O+C': 'Rewrite as subject + verb + object + complement',
          '其他': 'Rewrite as other pattern'
        }
      }))
    };
  }

  async generateFlashCards(topic, numberOfCards = 10, language = 'English', difficulty = 'intermediate', essayContent = null) {
    const startTime = Date.now();
    try {
      const prompt = `Generate ${numberOfCards} educational flash cards for the topic: "${topic}"

Language: ${language}
Difficulty: ${difficulty}
${essayContent ? `Essay Content: ${essayContent}` : ''}

IMPORTANT: Respond ONLY with valid JSON in this exact format:
{
  "title": "${topic}",
  "language": "${language}",
  "difficulty": "${difficulty}",
  "totalCards": ${numberOfCards},
  "cards": [
    {
      "id": 1,
      "question": "What is the main concept?",
      "answer": "The main concept is...",
      "explanation": "Detailed explanation of the concept",
      "category": "concept",
      "keywords": ["keyword1", "keyword2"],
      "imageQuery": "topic related image",
      "imageUrl": "https://source.unsplash.com/800x600/?topic,education",
      "imageAlt": "Educational illustration"
    }
  ]
}

CRITICAL: You must respond with ONLY valid JSON. Do not include any explanatory text, markdown formatting, or additional comments. The response must start with { and end with }. Only the JSON object is allowed.`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 3000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0].message.content;
      const endTime = Date.now();
      const elapsedTime = endTime - startTime;
      
      // Log timing and token information
      console.log(`DeepSeek API Flash Cards Generation - Elapsed Time: ${elapsedTime}ms`);
      if (data.usage) {
        console.log(`Tokens Used - Input: ${data.usage.prompt_tokens}, Output: ${data.usage.completion_tokens}, Total: ${data.usage.total_tokens}`);
      }
      
      // Debug: Log the raw response content
      console.log('Raw Flash Cards API Response:', content.substring(0, 500) + (content.length > 500 ? '...' : ''));
      
      try {
        const parsedResult = this.extractAndParseJSON(content);
        if (parsedResult) {
          return parsedResult;
        } else {
          console.warn('Failed to extract valid JSON from flash cards response, using fallback');
          return this.createFallbackFlashCards(topic, numberOfCards, language, difficulty);
        }
      } catch (parseError) {
        console.error('Flash cards JSON parsing error:', parseError);
        return this.createFallbackFlashCards(topic, numberOfCards, language, difficulty);
      }
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return this.createFallbackFlashCards(topic, numberOfCards, language, difficulty);
    }
  }

  createFallbackFlashCards(topic, numberOfCards, language, difficulty) {
    const cards = [];
    for (let i = 1; i <= numberOfCards; i++) {
      cards.push({
        id: i,
        question: `Question ${i} about ${topic}?`,
        answer: `Answer ${i} for ${topic}`,
        explanation: `This is a detailed explanation for question ${i}`,
        category: 'general',
        keywords: [topic.toLowerCase(), 'education', 'learning'],
        imageQuery: topic,
        imageUrl: `https://source.unsplash.com/800x600/?${topic},education`,
        imageAlt: `${topic} educational illustration`
      });
    }
    
    return {
      title: topic,
      language: language,
      difficulty: difficulty,
      totalCards: numberOfCards,
      cards: cards
    };
  }
}

export default new DeepSeekApiService(); 